import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { API, authFetch } from "../api";
import { useToast } from "./Toast";

const STEPS = ["Processing payment...", "Verifying with PayU...", "Sending WhatsApp notification...", "Confirming order..."];
const METHODS = [
  { id: "upi", label: "UPI / Google Pay / PhonePe", sub: "Instant payment" },
  { id: "card", label: "Debit / Credit Card", sub: "Visa, Mastercard, RuPay" },
  { id: "netbanking", label: "Net Banking", sub: "All major banks" },
  { id: "cash", label: "Pay at Counter", sub: "Cash on pickup" },
];

export default function PaymentScreen({ orderData, onBack, onSuccess }) {
  const { showToast } = useToast();
  const [method, setMethod] = useState("upi");
  const [processing, setProcessing] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [shopIsOpen, setShopIsOpen] = useState(true);
  const [shopLoading, setShopLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API}/shop-status`)
      .then(r => r.json())
      .then(data => { setShopIsOpen(data.is_open); setShopLoading(false); })
      .catch((e) => { console.error("Failed to fetch shop status:", e); showToast("Failed to fetch shop status", "error"); setShopLoading(false); });
  }, [showToast]);

  if (shopLoading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
      <div className="spinner" />
      <div style={{ fontSize: 14, color: "var(--mid)" }}>Checking availability...</div>
    </div>
  );

  if (!shopIsOpen) {
    return (
      <div style={{ background: "#FEF2F2", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>🔒</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#991B1B", marginBottom: 10 }}>Shop is Closed</div>
        <div style={{ fontSize: 14, color: "#666", marginBottom: 30 }}>We are not accepting orders at the moment. Please try again when we reopen.</div>
        <button onClick={onBack} className="btn-primary" style={{ padding: "12px 30px" }}>
          Back to Menu
        </button>
      </div>
    );
  }

  const handlePay = async () => {
    if (method === "cash") {
      setProcessing(true);
      setStepIdx(0);
      try {
        const res = await authFetch(`${API}/place-cash-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: orderData.items, tokenType: orderData.tokenType, specialInstructions: orderData.specialInstructions })
        });
        const result = await res.json();
        if (result.status === "success") {
          onSuccess({ ...orderData, order_id: result.order_id });
        } else {
          showToast(result.error || "Order failed. Please try again.", "error");
          setProcessing(false);
        }
      } catch {
        showToast("Cannot connect to server", "error");
        setProcessing(false);
      }
      return;
    }

    setProcessing(true);
    let i = 0;
    const iv = setInterval(() => { i++; setStepIdx(i); if (i >= STEPS.length - 1) clearInterval(iv); }, 900);

    try {
      // Get PayU order details from backend
      const orderRes = await authFetch(`${API}/payu/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData)
      });
      const orderDetails = await orderRes.json();

      if (!orderDetails.txnid) {
        throw new Error("Failed to create PayU order");
      }

      // Store order data in sessionStorage for later retrieval
      sessionStorage.setItem("pendingOrder", JSON.stringify(orderData));
      sessionStorage.setItem("pendingTxnId", orderDetails.txnid);

      // Create form and submit to PayU
      const form = document.createElement("form");
      form.method = "POST";
      form.action = orderDetails.payu_api_base + "/_payment";
      form.style.display = "none";

      const fields = {
        key: orderDetails.merchant_key,
        txnid: orderDetails.txnid,
        amount: orderDetails.amount,
        productinfo: orderDetails.productinfo,
        firstname: orderDetails.firstname,
        email: orderDetails.email,
        phone: orderDetails.phone,
        hash: orderDetails.hash,
        surl: `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/payu/success`,
        furl: `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/payu/failed`,
        service_provider: "payu_paisa"
      };

      for (const [key, value] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      showToast("Payment error: " + error.message, "error");
      setProcessing(false);
      clearInterval(iv);
    }
  };

  if (processing) return (
    <div className="processing">
      <div className="spinner" />
      <div className="proc-label">{STEPS[stepIdx]}</div>
      <div className="proc-sub">Please keep this screen open</div>
    </div>
  );

  return (
    <motion.div
      className="screen"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="screen-header">
        <button className="back-btn" onClick={onBack} aria-label="Go back">‹</button>
        <div className="screen-title">Payment</div>
      </div>
      <div className="screen-body">

        <div className="info-box">
          {orderData.items.map(i => (
            <div key={i.id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 13 }}>
              <span>{i.name} × {i.qty}</span><span>₹{i.price * i.qty}</span>
            </div>
          ))}
        </div>

        <div className="divider" />
        <div className="total-row" style={{ marginBottom: 20 }}>
          <span className="total-label">Amount to Pay</span>
          <span className="total-val">₹{orderData.total}</span>
        </div>

        <div className="field-label" style={{ marginBottom: 10 }}>Payment Method</div>
        <div>
          {METHODS.map(m => (
            <div key={m.id} onClick={() => setMethod(m.id)} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 16px", borderRadius: "var(--radius)", marginBottom: 10,
              border: method === m.id ? "2px solid var(--primary)" : "1.5px solid var(--border)",
              background: method === m.id ? "var(--primary-l)" : "white",
              cursor: "pointer", transition: "all 0.2s",
              boxShadow: method === m.id ? "0 4px 12px rgba(217,79,0,0.12)" : "none",
              transform: method === m.id ? "translateY(-1px)" : "none",
            }}>
              {/* Radio */}
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: method === m.id ? "6px solid var(--primary)" : "2px solid var(--border)", flexShrink: 0, transition: "all 0.2s" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: method === m.id ? "var(--primary)" : "var(--dark)" }}>{m.label}</div>
                <div style={{ fontSize: 11, color: "var(--mid)", marginTop: 1 }}>{m.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <button className="btn-primary" onClick={handlePay}>Pay ₹{orderData.total}</button>
        </div>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: "var(--mid)" }}>Secured by PayU</div>
      </div>
    </motion.div>
  );
}
